import type {
  BrandItem,
  ColorItem,
  LensShowItem,
  LensShowResponse,
} from '@/app/_lens-components/types';
import { isMergeBrandDropdownLens } from './lensMergeBrand';

/** 镜片类型唯一键：折叠展开与 localStorage `prevLensType` 第一段均为 `${lens_type}_${lens_name}` */
export function getLensTypeKey(lens: LensShowItem): string {
  return `${lens.lens_type}_${lens.lens_name}`;
}

export type ResolvedLensTypeColor = {
  lens: LensShowItem;
  brand: BrandItem | null;
  color: ColorItem;
};

/** 按「上次选中 key → 默认 key → 列表首项」解析选项 */
function pickLensOption<T>(
  list: T[],
  preferredKey: string,
  getKey: (item: T) => string | undefined,
  defaultKey?: string | null,
): T | undefined {
  if (!list.length) return undefined;
  if (preferredKey) {
    const byPreferred = list.find((item) => getKey(item) === preferredKey);
    if (byPreferred) return byPreferred;
  }
  if (defaultKey) {
    const byDefault = list.find((item) => getKey(item) === defaultKey);
    if (byDefault) return byDefault;
  }
  return list[0];
}

/**
 * 根据上次选择的镜片类型键 / 品牌 / 颜色，在 lens_show 列表中还原选中项。
 * `prev*` 全为空时，退化为列表第一项的默认品牌与颜色（与线上默认勾选兜底一致）。
 */
export function resolveLensTypeColorSelection(
  lensTypeColorData: LensShowResponse,
  prevLensTypeKey: string,
  prevLensBrandTitle: string,
  prevLensColor: string,
): ResolvedLensTypeColor | null {
  if (!lensTypeColorData?.length) return null;

  const lens = prevLensTypeKey
    ? lensTypeColorData.find((item) => getLensTypeKey(item) === prevLensTypeKey)
    : lensTypeColorData[0];
  if (!lens) return null;

  const hasBrandDropdown = isMergeBrandDropdownLens(lens) && !!lens.brand_list?.length;
  const brand = hasBrandDropdown
    ? pickLensOption(
        lens.brand_list!,
        prevLensBrandTitle,
        (b) => b.brand_title,
        lens.default_brand_title,
      ) || null
    : null;

  const colorList = brand?.color_list || lens.color_list;
  if ((hasBrandDropdown && !brand?.color_list?.length) || !colorList?.length) return null;
  const defaultLensColor = brand?.default_lens_color || lens.default_lens_color;
  const color = pickLensOption(colorList, prevLensColor, (c) => c.lens_color, defaultLensColor);
  return color ? { lens, brand, color } : null;
}
